/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {
  cloneJobInfo,
  jobTaskFilesNotFound,
  normalizeJobTasks
} from '@ion/applications/src/components/jobs/helpers';
import {showJobCreateEditor} from '@ion/applications/src/components/jobs/state';
import {clearDeviceSelection} from '@ion/applications/src/pages/devices/state';
import {error} from '@ion/core/src/logger/logger';
import {
  createJob,
  getJob,
  loadDevicesStatus,
  loadDownloadJobs,
  removeDevice,
  removeDownloadJob,
  removeTask
} from '@ion/core/src/services/Connect';
import {entityId, parseDevices} from '@ion/core/src/services/ConnectUtils';
import {
  Asset,
  AuditChange,
  DownloadJob,
  DownloadJobsFilters,
  DownloadJobType
} from '@ion/core/src/types/entities';
import {
  LoadDevicesStatusParams,
  LoadDownloadJobsParams
} from '@ion/core/src/types/services';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {PageInitializeAction} from '@/types/redux';

// name
export const NAME                                    = 'downloadjobs';

// initialise the grid
export const INITIALIZE_DOWNLOAD_JOBS                = 'INITIALIZE_DOWNLOAD_JOBS';
export const SET_GRID_COLUMNS                        = 'SET_DOWNLOAD_JOBS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                     = 'UPDATE_DOWNLOAD_GRID_COLUMNS';

// refresh grid ...
export const DOWNLOAD_JOBS_GRID_REFRESHED            = 'DOWNLOAD_JOBS_GRID_REFRESHED';

export const SET_VIEW_SUB_GROUPS                     = 'SET_DOWNLOAD_JOBS_VIEW_SUB_GROUPS';

export const SET_GRID_PAGE_SIZE                      = 'SET_DOWNLOAD_JOBS_GRID_PAGE_SIZE';

// retrieve Download Jobs
export const GET_DOWNLOAD_JOBS_REQUEST               = 'GET_DOWNLOAD_JOBS_REQUEST';
export const GET_DOWNLOAD_JOBS_RESPONSE              = 'GET_DOWNLOAD_JOBS_RESPONSE';
export const GET_DOWNLOAD_JOBS_ERROR                 = 'GET_DOWNLOAD_JOBS_ERROR';

//retrieve Download Devices Jobs
export const GET_DOWNLOAD_DEVICES_JOBS_REQUEST       = 'GET_DOWNLOAD_DEVICES_JOBS_REQUEST';
export const GET_DOWNLOAD_DEVICES_JOBS_RESPONSE      = 'GET_DOWNLOAD_DEVICES_JOBS_RESPONSE';
export const GET_DOWNLOAD_DEVICES_JOBS_ERROR         = 'GET_DOWNLOAD_DEVICES_JOBS_ERROR';

// search queries
export const UPDATE_DOWNLOAD_JOBS_SEARCH_QUERIES     = 'UPDATE_DOWNLOAD_JOBS_SEARCH_QUERIES';
export const RESET_DOWNLOAD_JOBS_SEARCH_QUERIES      = 'RESET_DOWNLOAD_JOBS_SEARCH_QUERIES';

// cancel Jobs
export const CANCEL_JOB                              = 'CANCEL_JOB';
export const CANCEL_JOB_REQUEST                      = 'CANCEL_JOB_REQUEST';
export const CANCEL_JOB_RESPONSE                     = 'CANCEL_JOB_RESPONSE';
export const CANCEL_JOB_ERROR                        = 'CANCEL_JOB_ERROR';

// cancel Tasks
export const CANCEL_TASK                             = 'CANCEL_TASK';
export const CANCEL_TASK_REQUEST                     = 'CANCEL_TASK_REQUEST';
export const CANCEL_TASK_RESPONSE                    = 'CANCEL_TASK_RESPONSE';
export const CANCEL_TASK_ERROR                       = 'CANCEL_TASK_ERROR';

// cancel Devices
export const CANCEL_DEVICE                           = 'CANCEL_DEVICE';
export const CANCEL_DEVICE_REQUEST                   = 'CANCEL_DEVICE_REQUEST';
export const CANCEL_DEVICE_RESPONSE                  = 'CANCEL_DEVICE_RESPONSE';
export const CANCEL_DEVICE_ERROR                     = 'CANCEL_DEVICE_ERROR';

// Audit Jobs
export const AUDIT_DOWNLOAD_JOBS                     = 'AUDIT_DOWNLOAD_JOBS';
export const AUDIT_DOWNLOAD_JOBS_SHOW_CHANGES_DIALOG = 'AUDIT_DOWNLOAD_JOBS_SHOW_CHANGES_DIALOG';

// rerun Jobs
export const DISPLAY_RERUN_JOB_DIALOG                = 'DISPLAY_RERUN_JOB_DIALOG';
export const RERUN_JOB_REQUEST                       = 'RERUN_JOB_REQUEST';
export const RERUN_JOB_RESPONSE                      = 'RERUN_JOB_RESPONSE';
export const RERUN_JOB_ERROR                         = 'RERUN_JOB_ERROR';

export const GET_DEVICES_STATUS_ERROR                = 'GET_DEVICES_STATUS_ERROR';
export const SET_DEVICE_STATUS_RESPONSE              = 'SET_DEVICE_STATUS_RESPONSE';
export const SHOW_DOWNLOAD_JOBS                      = 'SHOW_DOWNLOAD_JOBS';
export const SHOW_DEVICES_STATUS                     = 'SHOW_DEVICES_STATUS';
export const SHOW_DEVICES_STATUS_ERROR               = 'SHOW_DEVICES_STATUS_ERROR';
export const DOWNLOAD_JOBS                           = 'DOWNLOAD_JOBS';

/**
 * @downloadJobsGridRefreshed
 */
export const downloadJobsGridRefreshed = (): PageInitializeAction => {
  return {type: DOWNLOAD_JOBS_GRID_REFRESHED};
};

/**
 * @initialize
 */
export const initializeDownloadJobs = (): PageInitializeAction => {
  return {type: INITIALIZE_DOWNLOAD_JOBS};
};

// Updates the modular search queries for Device File
export const updateDownloadJobsSearchQueries = (searchParams: DownloadJobsFilters) => ({
  type : UPDATE_DOWNLOAD_JOBS_SEARCH_QUERIES, searchParams
});

// Resets the Device File filters state
export const resetDownloadJobsSearchQueries = () => ({
  type : RESET_DOWNLOAD_JOBS_SEARCH_QUERIES
});

export const showDownloadJobs = (downloadjobs: DownloadJob, show = true) => ({
  type : SHOW_DOWNLOAD_JOBS, downloadjobs, show
});

export const showDevicesStatus = (downloadjobs: DownloadJob, show = true) => ({
  type : SHOW_DEVICES_STATUS, downloadjobs, show
});

export const downloadJob = (downloadjobs: DownloadJob, show = true) => ({
  type : DOWNLOAD_JOBS, downloadjobs, show
});

/**
 * Invoked when the Cancel Job Dialog is to be displayed or removed
 */
export const cancelJobs = (downloadjobs: DownloadJob, show = true) => ({
  type : CANCEL_JOB, downloadjobs, show
});

/**
 * Invoked when the the Download Job auditor is to be displayed or removed.
 */
export const auditDownloadJob = (downloadjobs: DownloadJob, show = true) => ({
  type : AUDIT_DOWNLOAD_JOBS, downloadjobs, show
});

export const showAuditDownloadJobChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_DOWNLOAD_JOBS_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Invoked when an existing download job is to be cancelled, delegating to the cancelDownloadJob
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const cancelDownloadJob = (downloadjobs: DownloadJob) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: CANCEL_JOB_REQUEST});
    try {
      const downloadJobResult          = await getJob(downloadjobs?.id);
      downloadJobResult.progressStatus = DownloadJobType.Cancelling;
      const cancelled                  = await removeDownloadJob(downloadJobResult);
      dispatch({type: CANCEL_JOB_RESPONSE, downloadjobs: cancelled});
    } catch (err) {
      dispatch({type: CANCEL_JOB_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Cancel Task Dialog is to be displayed or removed
 */
export const cancelTasks = (downloadjobs: DownloadJob, index: number, show = true) => ({
  type : CANCEL_TASK, downloadjobs, index, show
});

/**
 * Invoked when an existing task is to be cancelled, delegating to the cancel task
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const cancelTask = (downloadjobs: DownloadJob, index: number) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: CANCEL_TASK_REQUEST});
    try {
      const requestBody = '"Cancelling"';
      const cancelled   = await removeTask(downloadjobs?.id, index, requestBody);
      dispatch({type: CANCEL_TASK_RESPONSE, downloadjobs: cancelled});
    } catch (err) {
      dispatch({type: CANCEL_TASK_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Cancel Device Dialog is to be displayed or removed
 */
export const cancelDevices = (downloadjobs: DownloadJob, devicestatus: Asset, show = true) => ({
  type : CANCEL_DEVICE, downloadjobs, devicestatus, show
});

/**
 * Invoked when an existing device is to be cancelled, delegating to the cancel device
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const cancelDevice = (device: any, downloadjob: DownloadJob, index: number) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: CANCEL_DEVICE_REQUEST});
    try {
      const downloadDeviceResult = await getJob(downloadjob?.id);
      const accountId: string    = entityId(downloadDeviceResult.account.href);
      const {task}               = downloadDeviceResult.tasks[index];
      const isMDTDevice          = device?.device?.category === 'MDT';
      const deviceIdentifier     = isMDTDevice ? 'serialNumber' : 'esn';
      const identifier           = isMDTDevice ? device?.device?.serialNumber : device?.esn;
      task.progressStatus        = DownloadJobType.Cancelling;
      task.criteria = [
        {
          memberSearch : {
            searchTerms : {
              account            : accountId.toString(),
              [deviceIdentifier] : identifier
            },
            hierLevel : 'ALL'
          }
        }
      ];
      const cancelled = await removeDevice(downloadDeviceResult);
      dispatch({type: CANCEL_DEVICE_RESPONSE, downloadjobs: cancelled});
    } catch (err) {
      dispatch({type: CANCEL_DEVICE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @getDownloadJobs
 *
 * fetches jobs for this account to be displayed in the Download Jobs
 * section
 */
export const getDownloadJobs = ({
  accountId,
  includeSubGroups,
  sort,
  filter,
  callback: callbackFn,
  pageId = 1,
  pageSize = 20
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_DOWNLOAD_JOBS_REQUEST});
    const {
      downloadjobs: {
        search
      }
    } = getState();
    const data: LoadDownloadJobsParams = {
      accountId,
      includeSubGroups,
      pageId,
      pageSize
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...createModularSearchFilters(search)
    };
    try {
      const {items, totalCount} = await loadDownloadJobs(data);
      dispatch({type: GET_DOWNLOAD_JOBS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      callbackFn?.({error: err, items: [], totalCount: 0});
      dispatch({type: GET_DOWNLOAD_JOBS_ERROR, error: err});
      error(err);
    }
  };
};

export const getDevicesStatus = ({
  devicesstatus,
  pageId = 1,
  pageSize = 20,
  callback: callbackFn,
  index
}) => {
  return async(dispatch: Function): Promise<any> => {
    try {
      const data: LoadDevicesStatusParams = {
        accountId : entityId(devicesstatus?._self.href),
        index,
        pageId,
        pageSize
      };
      const {items: result, totalCount} = await loadDevicesStatus(data);
      const items = parseDevices(result);
      callbackFn?.({items, totalCount});
      dispatch({type: SET_DEVICE_STATUS_RESPONSE, items});
      return {items, totalCount};
    } catch (err) {
      dispatch({type: GET_DEVICES_STATUS_ERROR, error: err});
      callbackFn?.({error: err, items: [], totalCount: 0});
      error(err);
    }
  };
};

// Dispatches an action to show or hide the Jobs rerun confirmation dialog
export const displayRerunJobDialog = (job: DownloadJob, show: boolean = true) => ({
  type : DISPLAY_RERUN_JOB_DIALOG, job, show
});

/**
 * Dispatched when a job has been selected to be rerun.
 */
export const rerunJob = (job: DownloadJob) => {
  return async(dispatch: Function): Promise<any> => {
    try {
      const rerunJob = await cloneJobInfo(job, true);
      if (jobTaskFilesNotFound(rerunJob)) {
        dispatch(clearDeviceSelection());
        dispatch(showJobCreateEditor(null, job, true));
      } else {
        const normalizedJob = normalizeJobTasks(rerunJob);
        dispatch({type: RERUN_JOB_REQUEST});
        const createdJob = await createJob({job: normalizedJob});
        dispatch({type: RERUN_JOB_RESPONSE, job: createdJob});
      }
    } catch (err) {
      dispatch({type: RERUN_JOB_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setDownloadJobsViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setDownloadJobsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});
